import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import moment from 'moment';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import {parseSearchDates} from '../../util/dates';
import { richText } from '../../util/richText';
import { createSlug, parse } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, FavoriteListing } from '../../components';

import { types as sdkTypes } from '../../util/sdkLoader';
import css from './ListingCard.module.css';
const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });


const calculateUnitPrice = (search,listing,unitPrice) => {

  const {startDate,endDate} =  parseSearchDates({search,listing});
  const {dayPriceTiers} = listing?.attributes?.publicData;

  const dayDiff =
          startDate && endDate
            ? parseInt(moment(endDate).diff(moment(startDate), 'day'))
            : 1;
        const selectedUnitPriceRaw =
          dayPriceTiers?.find?.(
            priceTier => dayDiff >= priceTier.firstItem && dayDiff <= priceTier.lastItem
          ) ?? dayPriceTiers?.[0];

        const selectedUnitPrice = selectedUnitPriceRaw
          ? new Money(
              selectedUnitPriceRaw.price.amount,
              selectedUnitPriceRaw.price.currency
            )
          : unitPrice;

          return selectedUnitPrice;
};

const PriceMaybe = props => {
  const { price, originalPrice, publicData, config, intl, listing, isAuthenticated } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = isAuthenticated && displayPrice(foundListingTypeConfig);

  if (!showPrice && originalPrice) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(originalPrice, config.currency, intl);
  const {formattedPrice : newPrice} =priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>       
        {newPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit} style={{ marginRight: '5px' }}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
      {originalPrice.amount != price.amount && (
        <div className={css.priceValue} title={priceTitle} style={{ color: '#777', textDecoration: 'line-through' }}>
          {formattedPrice}
        </div>
      )}
      <FavoriteListing className={css.favoriteListing} listing={listing} />
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isAuthenticated,
  } = props;
  const location = useLocation();
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const { dates, "pub_country": countriesParam } = parse(location.search);

  const selectedPrice = (publicData.dayPriceTiers?.[0]
    ? new Money(
        publicData.dayPriceTiers?.[0].price.amount,
        publicData.dayPriceTiers?.[0].price.currency
      )
    : price) ?? {};

    const finalPrice = calculateUnitPrice(location.search,currentListing,selectedPrice);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      to={{ search: [].concat(dates ? [`dates=${dates}`] : []).concat(countriesParam ? [`pub_country=${countriesParam}`] : []).join("&") }}
    >
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <PriceMaybe
          price={finalPrice}
          originalPrice={selectedPrice}
          publicData={publicData}
          config={config}
          intl={intl}
          listing={listing}
          isAuthenticated={isAuthenticated}
        />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: false,
  isAuthenticated: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
  isAuthenticated: bool,
};

export default injectIntl(ListingCardComponent);
